import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { RootState } from '@/store';
import { API_ENDPOINTS, API_SLICE, BASE_URL } from '@/utils/constants/apiEndpoints';

import { User, UserRole } from '@/types/user';

interface FeatureFlag {
  shortCode: string;
  setting: number;
}
export interface AdminFeatureFlag {
  id: string;
  displayName: string;
  displaySummary: string;
  shortCode: string;
  setting: number;
  extendedSetting: any;
  createdAt: string;
  updatedAt: string;
  createdBy: User;
  updatedBy: User;
  updated_by: string;
}

export interface AdminFeatureFlagResponse {
  data: AdminFeatureFlag[];
}

const getRoleSetting = (role?: UserRole): number => {
  const internalTestingRoles = [UserRole.InternalSuperAdmin, UserRole.InternalAdmin];

  const internalRoles = [
    UserRole.InternalSuperAdmin,
    UserRole.InternalAdmin,
    UserRole.InternalClinical,
    UserRole.InternalNonClinical,
    UserRole.InternalApiAccess
  ];

  if (role && internalTestingRoles.includes(role)) {
    return 2;
  }
  if (role && internalRoles.includes(role)) {
    return 3;
  }
  return 5;
};

export const featureFlagsApiSlice = createApi({
  reducerPath: 'featureFlagsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: BASE_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    }
  }),
  tagTypes: Object.values(API_SLICE),
  endpoints: (builder) => ({
    getAllFlags: builder.query<string[], void>({
      query: () => ({
        url: API_ENDPOINTS.FEATURE_FLAGS.LIST,
        method: 'GET'
      }),
      transformResponse: (response: FeatureFlag[]) => {
        const currentUserData = localStorage.getItem('persist:root');
        let userRoleSetting = 5;

        if (currentUserData) {
          const authObj = JSON.parse(currentUserData);
          const currentUser = JSON.parse(authObj?.auth || '{}');
          userRoleSetting =
            currentUser?.user?.featureFlagRole || getRoleSetting(currentUser?.role as UserRole);
        }

        const flags =
          response
            .filter((flag) => {
              if (userRoleSetting < 2) return false;
              return userRoleSetting <= flag.setting;
            })
            .map((flag) => flag.shortCode) ?? [];

        return flags;
      }
    })
  })
});

export const { useGetAllFlagsQuery } = featureFlagsApiSlice;

export const adminFeatureFlagsApiSlice = createApi({
  reducerPath: 'adminFeatureFlagsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: BASE_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    }
  }),
  tagTypes: Object.values(API_SLICE),
  endpoints: (builder) => ({
    getAllAdminFlags: builder.query<AdminFeatureFlagResponse, void>({
      query: () => ({
        url: API_ENDPOINTS.FEATURE_FLAGS.ADMIN,
        method: 'GET'
      }),
      providesTags: [{ type: API_SLICE.FeatureFlags, id: 'LIST' }]
    }),

    updateAdminFeatureFlags: builder.mutation<
      AdminFeatureFlagResponse, // response type
      { id: string; setting: string }[] // payload type
    >({
      query: (body) => ({
        url: API_ENDPOINTS.FEATURE_FLAGS.UPDATE,
        method: 'PUT',
        body
      }),
      invalidatesTags: [{ type: API_SLICE.FeatureFlags, id: 'LIST' }]
    })
  })
});

export const { useGetAllAdminFlagsQuery, useUpdateAdminFeatureFlagsMutation } =
  adminFeatureFlagsApiSlice;

// Custom hook to check if a feature flag is enabled
export function useFeatureFlag(shortCode: string, defaultValue: boolean = false): boolean {
  const { data: featureFlags } = useGetAllFlagsQuery();

  return featureFlags?.includes(shortCode) ?? defaultValue;
}
